import {UserLogged} from '../payloads/UserLogged';

/** Backend sign-in MFA challenge (`auth/v1/admins/signIn` → `data` payload). */
export type AdminSignInMfaChallenge = {
  enabledMFA: true;
  preferredMFAType?: string;
  preferredMFType?: string;
  emailFormat?: string | null;
  formatPhoneNumber?: string | null;
  /** Enrolled factors the user may use for this sign-in (e.g. `SMS_MFA`, `EMAIL_MFA`, `TOTP_MFA`). */
  availableMfaMethods?: string[];
  /** Present when API supports “remember this device” for MFA (feature-flagged server-side). */
  authSecurity?: {
    trustedDeviceMfa?: boolean;
    rememberDeviceDays?: number;
  };
};

function unwrapResponseData(body: unknown): Record<string, unknown> | null {
  if (body == null || typeof body !== 'object') {
    return null;
  }
  const root = body as Record<string, unknown>;
  const inner = root['data'];
  if (inner != null && typeof inner === 'object') {
    return inner as Record<string, unknown>;
  }
  return root;
}

/** If sign-in returned MFA challenge (no tokens), return the payload; otherwise null. */
export function parseAdminSignInMfaChallenge(body: unknown): AdminSignInMfaChallenge | null {
  const payload = unwrapResponseData(body);
  if (!payload) {
    return null;
  }
  if (payload['enabledMFA'] !== true) {
    return null;
  }
  const hasToken =
    (typeof payload['token'] === 'string' && payload['token'].toString().trim() !== '') ||
    (typeof payload['accessToken'] === 'string' && payload['accessToken'].toString().trim() !== '');
  if (hasToken) {
    return null;
  }
  return payload as unknown as AdminSignInMfaChallenge;
}

function isTruthyMfaFlag(v: unknown): boolean {
  if (v === true || v === 1) {
    return true;
  }
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }
  return false;
}

function readRememberDeviceDays(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
    return Math.floor(v);
  }
  if (typeof v === 'string' && /^\d+$/.test(v.trim())) {
    const n = parseInt(v.trim(), 10);
    return n > 0 ? n : undefined;
  }
  return undefined;
}

/**
 * Reads `trustedDeviceMfa` / `rememberDeviceDays` from an object or its nested `authSecurity`
 * (supports snake_case variants). Returns whether MFA trust was explicitly set false.
 */
function readTrustedDeviceHints(obj: unknown): {
  trusted?: boolean;
  days?: number;
  explicitFalse: boolean;
} {
  if (obj == null || typeof obj !== 'object') {
    return { explicitFalse: false };
  }
  const o = obj as Record<string, unknown>;
  const nested = o['authSecurity'];
  let trusted: boolean | undefined;
  let days: number | undefined;
  let explicitFalse = false;

  const scan = (rec: Record<string, unknown>) => {
    const td = rec['trustedDeviceMfa'] ?? rec['trusted_device_mfa'];
    if (td !== undefined && td !== null) {
      if (isTruthyMfaFlag(td)) {
        trusted = true;
      } else if (td === false || td === 0 || td === '0' || String(td).toLowerCase() === 'false') {
        explicitFalse = true;
      }
    }
    const d = readRememberDeviceDays(rec['rememberDeviceDays'] ?? rec['remember_device_days']);
    if (d != null) {
      days = d;
    }
  };

  scan(o);
  if (nested != null && typeof nested === 'object') {
    scan(nested as Record<string, unknown>);
  }

  return { trusted, days, explicitFalse };
}

export type MfaAuthSecurityResolved = {
  /** Whether to show “trust this device” during MFA. */
  trustedDeviceMfa: boolean;
  rememberDeviceDays: number;
};

export type ResolveMfaAuthSecurityOptions = {
  /** When API omits `rememberDeviceDays`, use this (should match server `AUTH_TRUSTED_DEVICE_DAYS`). */
  defaultRememberDays?: number;
};

/**
 * Merges `authSecurity` from the MFA challenge and the full sign-in HTTP body.
 * Some APIs nest flags only under `data`, others attach `authSecurity` next to `data` at the root.
 */
export function resolveMfaAuthSecurity(
  signInBody: unknown,
  challenge: AdminSignInMfaChallenge,
  options?: ResolveMfaAuthSecurityOptions,
): MfaAuthSecurityResolved {
  const fromChallenge = readTrustedDeviceHints(challenge);
  const payload = unwrapResponseData(signInBody);
  const fromPayload = readTrustedDeviceHints(payload);
  const fromRoot = readTrustedDeviceHints(signInBody);

  const trustedExplicitTrue =
    fromChallenge.trusted === true || fromPayload.trusted === true || fromRoot.trusted === true;
  const anyExplicitFalse =
    fromChallenge.explicitFalse || fromPayload.explicitFalse || fromRoot.explicitFalse;

  const days =
    fromChallenge.days ?? fromPayload.days ?? fromRoot.days;

  let trustedDeviceMfa = false;
  if (trustedExplicitTrue) {
    trustedDeviceMfa = true;
  } else if (!anyExplicitFalse && days != null && days > 0) {
    trustedDeviceMfa = true;
  }

  const fallbackDays = options?.defaultRememberDays ?? 14;

  return {
    trustedDeviceMfa,
    rememberDeviceDays: days ?? fallbackDays,
  };
}

/** Same resolution for an authenticated user profile (e.g. MFA setup page). */
export function resolveMfaAuthSecurityFromUserProfile(
  user: unknown,
  options?: ResolveMfaAuthSecurityOptions,
): MfaAuthSecurityResolved {
  const fallbackDays = options?.defaultRememberDays ?? 14;
  if (user == null || typeof user !== 'object') {
    return { trustedDeviceMfa: false, rememberDeviceDays: fallbackDays };
  }
  const top = user as Record<string, unknown>;
  const inner = top['data'];
  const profile = inner != null && typeof inner === 'object' ? inner : top;
  const r = readTrustedDeviceHints(profile);
  const trustedDeviceMfa = r.trusted === true || (!r.explicitFalse && r.days != null && r.days > 0);
  return {
    trustedDeviceMfa,
    rememberDeviceDays: r.days ?? fallbackDays,
  };
}

export function getMfaPreferredType(challenge: AdminSignInMfaChallenge): string {
  const raw = challenge.preferredMFAType ?? challenge.preferredMFType ?? '';
  return String(raw).trim().toUpperCase();
}

const KNOWN_MFA_METHODS = new Set(['SMS_MFA', 'EMAIL_MFA', 'TOTP_MFA']);

/** Same priority as backend `sortCanonicalMfaMethods` (TOTP → SMS → Email). */
export const MFA_METHOD_CANONICAL_ORDER = ['TOTP_MFA', 'SMS_MFA', 'EMAIL_MFA'] as const;

export function sortCanonicalMfaMethodIds(methods: string[]): string[] {
  const upper = new Set(methods.map((x) => String(x ?? '').trim().toUpperCase()));
  return MFA_METHOD_CANONICAL_ORDER.filter((id) => upper.has(id));
}

/** Normalizes `availableMfaMethods` from the API, falling back to the active preference when absent (older APIs). */
export function parseAvailableMfaMethodsFromChallenge(
  challenge: AdminSignInMfaChallenge,
  fallbackPreferred: string,
): string[] {
  const raw = challenge.availableMfaMethods;
  const preferred = String(fallbackPreferred || '').trim().toUpperCase();
  if (!Array.isArray(raw) || raw.length === 0) {
    return sortCanonicalMfaMethodIds(preferred && KNOWN_MFA_METHODS.has(preferred) ? [preferred] : []);
  }
  const normalized = [
    ...new Set(
      raw
        .map((x) => String(x ?? '').trim().toUpperCase())
        .filter((x) => KNOWN_MFA_METHODS.has(x)),
    ),
  ];
  if (normalized.length === 0) {
    return sortCanonicalMfaMethodIds(preferred && KNOWN_MFA_METHODS.has(preferred) ? [preferred] : []);
  }
  return sortCanonicalMfaMethodIds(normalized);
}

/** SMS/email OTP delivery at login (not authenticator TOTP). */
export function isLoginOtpDeliveryMfa(preferredUpper: string): boolean {
  return preferredUpper === 'SMS_MFA' || preferredUpper === 'EMAIL_MFA';
}

export function isLoginTotpMfa(preferredUpper: string): boolean {
  return preferredUpper === 'TOTP_MFA';
}

/** True when admin profile indicates MFA is configured (TOTP/SMS or explicitly enabled). */
export function hasAdminMfaConfigured(userLogged: UserLogged | null | undefined): boolean {
  if (!userLogged) {
    return false;
  }
  const dynamicUser = userLogged as UserLogged & { mfaEnabled?: boolean };
  if (dynamicUser.enabledMFA === true || dynamicUser.mfaEnabled === true) {
    return true;
  }

  const preferred = String(userLogged.preferredMFType ?? '').trim().toUpperCase();
  if (preferred && preferred !== 'NO_MFA' && preferred !== 'NO-MFA' && preferred !== 'NONE') {
    return true;
  }

  const challenge = String(userLogged.userChallenge ?? '').trim().toUpperCase();
  return challenge === 'SMS_MFA' || challenge === 'TOTP_MFA' || challenge === 'MFA_SETUP';
}

/** Normalize `setupMFATimeBasedOTP` / Cognito-style responses into a base32 secret for display / otpauth URL. */
export function extractTotpSecretFromSetupResponse(res: unknown): string | null {
  if (res == null) {
    return null;
  }
  const root = typeof res === 'object' && res !== null ? (res as Record<string, unknown>) : null;
  const d = root?.['data'] ?? root;
  if (typeof d === 'string' && d.replace(/\s/g, '').length >= 8) {
    return d.trim();
  }
  if (d == null || typeof d !== 'object') {
    return null;
  }
  const obj = d as Record<string, unknown>;
  const candidates = [
    obj['secretCode'],
    obj['SecretCode'],
    obj['sharedSecret'],
    obj['code'],
    obj['secret'],
    (obj['CodeDeliveryDetails'] as Record<string, unknown> | undefined)?.['Destination'],
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.replace(/\s/g, '').length >= 8) {
      return c.trim();
    }
  }
  return null;
}

/** `otpauth://` URL from `setupMFATimeBasedOTP` when provided by API. */
export function extractOtpauthUrlFromSetupResponse(res: unknown): string | null {
  if (res == null) {
    return null;
  }
  const root = typeof res === 'object' && res !== null ? (res as Record<string, unknown>) : null;
  const d = root?.['data'] ?? root;
  if (d == null || typeof d !== 'object') {
    return null;
  }
  const url = (d as Record<string, unknown>)['otpauthUrl'];
  return typeof url === 'string' && url.startsWith('otpauth://') ? url : null;
}
